import { Dispatch, SetStateAction, useContext } from "react";
import { PersonDto } from "../../../../../../interfaces/schema";
import StamopIcon from "../../../../../icons/StamopIcon";
import Button from "@/components/bricks/button/Button";
import Spinner from "../../../../../spinner/Spinner";
import { useMutation } from "@tanstack/react-query";
import { patch } from "../../../../../../api/util";
import { getPatchOperations } from "../../../../../../util/form";
import { dateToyyyyMMdd } from "../../../../../../util/date";
import CloseIcon from "../../../../../icons/CloseIcon";
import EditIcon from "../../../../../icons/EditIcon";
import { usePersonStamoFormik } from "./usePersonStamoFormik";
import PersonStamoFieldset1 from "./PersonStamoFieldset1";
import PersonStamoFieldset2 from "./PersonStamoFieldset2";
import { isDate } from "lodash";
import { isSameDay } from "date-fns";
import { UserQueryContext } from "../../../../../../context/UserContext";
import { PagedPersonQueryContext } from "../../../../../../context/PagedPersonQueryProvider";

const StamoplysningerTop = ({
  person,
  editMode,
  setEditMode,
  invalidateData,
  setToggleCpr,
  toggleCpr,
}: {
  person: PersonDto;
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  invalidateData: InvalidateDataFunction<any>;
  toggleCpr: boolean;
  setToggleCpr: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { user, hasPermission } = useContext(UserQueryContext);
  const { refetch } = useContext(PagedPersonQueryContext);
  const {
    isPending: isLoading,
    mutate,
    error: mutationError,
    reset: resetMutation,
  } = useMutation<unknown, { status: number }, Record<string, any>, unknown>({
    onSuccess: async function () {
      await invalidateCpr();
      await invalidateData();
      setEditMode(false);
      setToggleCpr(false);
    },
    mutationFn: (values: UpdatePersonDTO) => {
      const body = JSON.stringify(getPatchOperations(values));
      return patch({ query: `person/${person.id}`, body });
    },
    onError: () => {
      setTimeout(() => {
        resetMutation();
      }, 5000);
    },
  });
  const { formik, invalidateCpr, canReadCpr } = usePersonStamoFormik(
    person,
    isLoading,
    setEditMode,
    mutate,
    toggleCpr,
  );

  return (
    <form
      className={`c-card c-card--editable${editMode ? " is-editable" : ""}`}
      onSubmit={async (e) => {
        await formik.handleSubmit(e);
        // yes
        if (refetch) await refetch();
        if (refetch) await refetch();
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3 mt-1">
        <h3 className="d-flex align-items-center gap-2">
          <span className="c-theme-icon bg-primary-light text-primary">
            <StamopIcon />
          </span>{" "}
          Stamoplysninger
        </h3>
        {hasPermission(user, "BasisWrite") && (
          <Button
            type="button"
            transparent
            color="primary"
            onClick={() => {
              if (editMode) formik.resetForm();
              // ADV-349: CPR-nummeret hentes kun til redigering for brugere med CprRead.
              else if (canReadCpr) setToggleCpr(true);
              setEditMode(!editMode);
            }}
            className="edit-trigger"
          >
            {editMode && (
              <>
                Luk <CloseIcon />
              </>
            )}
            {!editMode && (
              <>
                Redigér
                <EditIcon />
              </>
            )}
          </Button>
        )}
      </div>

      <div className="row g-4">
        <PersonStamoFieldset1
          editMode={editMode}
          formik={formik}
          isLoading={isLoading}
          person={person}
          setToggleCpr={setToggleCpr}
          toggleCpr={toggleCpr}
          canReadCpr={canReadCpr}
        />
        <PersonStamoFieldset2
          editMode={editMode}
          formik={formik}
          isLoading={isLoading}
          person={person}
        />

        {editMode && (
          <>
            <div className="d-grid gap-2 d-md-flex justify-content-md-end">
              <Button
                type="button"
                color="secondary"
                className="me-md-2"
                onClick={() => {
                  formik.resetForm();
                  setEditMode(!editMode);
                  setToggleCpr(false);
                }}
              >
                Annuller
              </Button>
              <Button
                disabled={!formik.dirty || isLoading}
                type="submit"
                color="primary"
                onClick={() => {}}
              >
                {isLoading && <Spinner className="text-light" size={16} />} Gem
              </Button>
            </div>
            {mutationError && (
              <div className="d-flex justify-content-end">
                <small className={`text-danger mt-1 fw-semibold`}>
                  {`Noget gik galt.${
                    mutationError.status
                      ? " Fejlkode " + mutationError.status
                      : ""
                  }`}
                </small>
              </div>
            )}
          </>
        )}
      </div>
    </form>
  );
};

export function getUpdatePersonDTO(
  initialValues: IUpdatePersonFormik,
  newValues: IUpdatePersonFormik,
): UpdatePersonDTO {
  const updatePersonDTO: UpdatePersonDTO = {};
  for (const key of Object.keys(initialValues)) {
    let equal = initialValues[key] === newValues[key];
    if (isDate(initialValues[key]) || isDate(newValues[key])) {
      equal = isSameDay(initialValues[key], newValues[key]);
    }
    if (!equal) {
      const newValue = newValues[key];
      if (newValue instanceof Date) {
        updatePersonDTO[key] = dateToyyyyMMdd(newValue);
      } else {
        updatePersonDTO[key] = newValue ? newValue : null;
      }
    }
  }
  return updatePersonDTO;
}

export default StamoplysningerTop;
